import useSWR, { type SWRConfiguration } from 'swr';

export interface PolyValueData {
  user: string;
  value: number;
}

export type PolyValueRawResponse = PolyValueData[];

export interface PolyValueResult {
  value: number;
  detail: Record<string, number>;
}

export interface FetchPolyValueParams {
  user?: string;
  wallets?: string[];
}

export interface UsePolyValueOptions extends FetchPolyValueParams {
  enabled?: boolean;
  swr?: SWRConfiguration<PolyValueResult, Error>;
}

const POLY_VALUE_ENDPOINT = 'https://data-api.polymarket.com/value';

export async function fetchPolyValue({
  user,
  wallets,
}: FetchPolyValueParams): Promise<PolyValueResult> {
  const users = normalizeWallets(user ? [user] : (wallets ?? []));
  if (users.length === 0) {
    return { value: 0, detail: {} };
  }

  const entries = await Promise.all(
    users.map(async (userAddress) => {
      const searchParams = new URLSearchParams({
        user: userAddress,
      });

      const response = await fetch(`${POLY_VALUE_ENDPOINT}?${searchParams.toString()}`, {
        headers: {
          accept: 'application/json, text/plain, */*',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Polymarket value: ${response.status}`);
      }

      const raw = (await response.json()) as PolyValueRawResponse;
      return [userAddress, normalizeValueResponse(raw)] as const;
    }),
  );

  const detail = Object.fromEntries(entries);
  const value = entries.reduce((sum, [, walletValue]) => sum + walletValue, 0);

  return { value, detail };
}

export function usePolyValue(options: UsePolyValueOptions = {}) {
  const wallets = normalizeWallets(options.user ? [options.user] : (options.wallets ?? []));
  const enabled = options.enabled ?? true;
  const swrKey = enabled && wallets.length > 0 ? ['poly-value', wallets.join(',')] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<PolyValueResult, Error>(
    swrKey,
    () => fetchPolyValue({ wallets }),
    {
      revalidateOnFocus: false,
      ...options.swr,
    },
  );

  return {
    data: data ?? null,
    value: data?.value ?? 0,
    detail: data?.detail ?? {},
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeValueResponse(response: unknown): number {
  if (Array.isArray(response)) {
    return response.reduce((sum, item) => sum + normalizeValueResponse(item), 0);
  }

  if (response && typeof response === 'object') {
    const record = response as Record<string, unknown>;
    const value = record.value ?? record.currentValue ?? record.amount ?? record.data ?? undefined;
    return normalizeValueResponse(value);
  }

  return toNumber(response);
}

function normalizeWallets(wallets: string[]) {
  return [...new Set(wallets.map((wallet) => wallet.trim().toLowerCase()).filter(Boolean))];
}

function toNumber(value: unknown) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
}

export default usePolyValue;
